import { Check } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { idealProfileProgress, type IdealProfile } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { IDEAL_PROFILE_STEP_BOXES, IdealProfileForm, idealProfileStepsFor, type IdealProfileStep } from './TemplateForms';
import styles from './IdealProfileSteps.module.css';

/**
 * Evening review 2026-11-01 item 6: the Ideal candidate profile one box at a time, Next → Next, so it doesn't look like one long
 * page. The step list shows where you are and which steps are done; any step can be opened directly, and every box can be
 * skipped. ADR 0092: the same steps on Request a hire and on the job, where HR edits and completes them.
 */

const STEP_NAMES: Readonly<Record<IdealProfileStep, string>> = {
  tools: 'Tools',
  mustHaves: 'Must-haves',
  niceToHaves: 'Nice-to-haves',
  experience: 'Experience',
  qualifications: 'Qualifications',
  dealBreakers: 'Deal-breakers',
  workHabits: 'Work habits',
  redFlags: 'Red flags',
  leadership: 'Leading people',
};

/** The boxes with a minimum of 0: nothing in them is not "done". */
const OPTIONAL_STEPS: ReadonlySet<IdealProfileStep> = new Set(['niceToHaves', 'qualifications', 'dealBreakers', 'redFlags']);

interface IdealProfileStepsProps {
  readonly value: IdealProfile;
  readonly onChange: (value: IdealProfile) => void;
  readonly managesPeople: boolean;
  /** ADR 0087: the tool names offered in box 1. */
  readonly toolNames?: readonly string[];
  /** Read only: the steps still open, the boxes can't be changed. */
  readonly disabled?: boolean;
}

export function IdealProfileSteps({ value, onChange, managesPeople, toolNames, disabled = false }: IdealProfileStepsProps): ReactNode {
  const steps = idealProfileStepsFor(managesPeople);
  const [picked, setPicked] = useState<IdealProfileStep>('tools');
  const step = steps.includes(picked) ? picked : 'tools';
  const at = steps.indexOf(step);
  const next = steps[at + 1];
  const previous = steps[at - 1];
  const progress = idealProfileProgress(value, managesPeople);
  const isDone = (key: IdealProfileStep): boolean => {
    const boxes = IDEAL_PROFILE_STEP_BOXES[key];
    if (progress.problems.some((problem) => boxes.includes(problem.box))) return false;
    // A required box is done once it has no problem left; an optional one once something is in it.
    return !OPTIONAL_STEPS.has(key) || rowsIn(value, key) > 0;
  };
  return (
    <div className={styles.steps}>
      <nav aria-label="Ideal candidate steps">
        <ol className={styles.list}>
          {steps.map((key, index) => (
            <li key={key}>
              <button type="button" className={styles.step} aria-current={key === step ? 'step' : undefined} onClick={() => setPicked(key)}>
                <span className={styles.stepNumber}>{isDone(key) ? <Check size={12} aria-label="done" /> : index + 1}</span>
                {STEP_NAMES[key]}
              </button>
            </li>
          ))}
        </ol>
      </nav>
      <p className={styles.count}>
        Step {at + 1} of {steps.length}.{disabled ? '' : ' Skip anything you can’t answer.'}
      </p>
      <IdealProfileForm value={value} managesPeople={managesPeople} step={step} toolNames={toolNames} disabled={disabled} onChange={onChange} />
      <div className={styles.nav}>
        {previous && (
          <Button variant="ghost" onClick={() => setPicked(previous)}>
            Back
          </Button>
        )}
        {next && (
          <Button variant="outline" onClick={() => setPicked(next)}>
            Next: {STEP_NAMES[next]}
          </Button>
        )}
      </div>
    </div>
  );
}

/** How many rows an optional step's box has filled in. */
function rowsIn(profile: IdealProfile, key: IdealProfileStep): number {
  switch (key) {
    case 'niceToHaves':
      return profile.niceToHaves.filter((row) => row.text.trim() !== '').length;
    case 'qualifications':
      return profile.qualifications.filter((row) => row.text.trim() !== '').length;
    case 'dealBreakers':
      return profile.dealBreakers.filter((row) => row.text.trim() !== '').length;
    case 'redFlags':
      return profile.redFlags.filter((row) => row.text.trim() !== '').length;
    default:
      return 0;
  }
}
